import { z } from '@repo/schema'
import { formatStreetAddress } from '@repo/sdk/helpers'

import type { Coordinates } from './google-maps-input.ts'

export const addressComponentSchema = z.object({
  long_name: z.string(),
  short_name: z.string(),
  types: z.string().array(),
})

export const googlePlaceSchema = z.object({
  address_components: addressComponentSchema.array(),
  formatted_address: z.string().default(''),
  geometry: z.object({ location: z.object({ lat: z.number(), lng: z.number() }) }),
  name: z.string().nullish(),
  place_id: z.string(),
})

/** The geolocation service's own reverse geocode answer. */
const serviceAddressSchema = z.object({
  address: z.string().nullish(),
  city: z.string().nullish(),
  country: z.string().nullish(),
  country_code: z.string().nullish(),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
  place_id: z.string().nullish(),
  place_name: z.string().nullish(),
  postcode: z.string().nullish(),
})

export const reverseGeocodeSchema = z.union([
  z.object({ results: googlePlaceSchema.array() }),
  serviceAddressSchema,
])

export type AddressComponent = z.infer<typeof addressComponentSchema>

export type NormalizedAddress = {
  /** Street line: route and street number, ordered by the country's convention. */
  addressLine: string
  city: string
  /** Country name, e.g. `United Kingdom`. */
  country: string
  /** ISO 3166-1 alpha-2 code, e.g. `GB`. */
  countryCode: string
  lat: number
  lng: number
  name: string
  postcode: string
}

export type NormalizedPlace = {
  address: NormalizedAddress
  place: PlaceIdentity
}

export type PlaceDetails = NormalizedPlace & {
  /** Raw Google components, kept for display formatting with `addressFormatter`. */
  addressComponents: AddressComponent[]
}

export type PlaceIdentity = {
  formatted_address: string
  name: string
  place_id: string
}

export const normalizeGooglePlace = (
  result: z.output<typeof googlePlaceSchema>,
): NormalizedPlace => {
  const getComponent = (...types: string[]) =>
    types
      .map(type => result.address_components.find(component => component.types.includes(type)))
      .find(component => component !== undefined)
  const route = getComponent('route')?.long_name ?? ''
  const country = getComponent('country')
  const name = result.name?.trim() ?? ''

  return {
    address: {
      addressLine: formatStreetAddress({
        countryCode: country?.short_name,
        route,
        streetNumber: getComponent('street_number')?.long_name,
      }),
      city: getComponent('postal_town', 'locality', 'neighborhood')?.long_name ?? '',
      country: country?.long_name ?? '',
      countryCode: country?.short_name ?? '',
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng,
      name: name || getComponent('establishment', 'point_of_interest')?.long_name || route,
      postcode: getComponent('postal_code')?.long_name ?? '',
    },
    place: { formatted_address: result.formatted_address, name, place_id: result.place_id },
  }
}

export const normalizePlaceDetails = (
  result: z.output<typeof googlePlaceSchema>,
): PlaceDetails => ({
  ...normalizeGooglePlace(result),
  addressComponents: result.address_components,
})

const normalizeServiceAddress = (
  result: z.output<typeof serviceAddressSchema>,
  coordinates: Coordinates,
): NormalizedPlace | null => {
  const placeName = result.place_name ?? ''
  if (!result.address && !result.city && !result.postcode && !result.country && !placeName) {
    return null
  }

  const country = result.country ?? ''
  const [streetLine = ''] = placeName.split(',')

  return {
    address: {
      addressLine: result.address || streetLine.trim(),
      city: result.city ?? '',
      country: country.length === 2 ? '' : country,
      countryCode: result.country_code || (country.length === 2 ? country : ''),
      lat: result.lat ?? coordinates.lat,
      lng: result.lng ?? coordinates.lng,
      name: '',
      postcode: result.postcode ?? '',
    },
    place: { formatted_address: placeName, name: '', place_id: result.place_id ?? '' },
  }
}

/** `coordinates` are the reverse geocoded ones, used when the answer has none. */
export const normalizeReverseGeocode = (
  body: z.output<typeof reverseGeocodeSchema>,
  coordinates: Coordinates,
): NormalizedPlace | null => {
  if (!('results' in body)) return normalizeServiceAddress(body, coordinates)

  const [result] = body.results
  if (!result) return null

  const normalized = normalizeGooglePlace(result)

  return {
    ...normalized,
    address: {
      ...normalized.address,
      addressLine: normalized.address.addressLine || result.formatted_address,
    },
  }
}
